// src/components/notifications/notification-item.tsx

// @mui
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
// import Avatar from '@mui/material/Avatar'; // Không dùng thì bỏ hoặc giữ nếu cần
import Typography from '@mui/material/Typography';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemButton from '@mui/material/ListItemButton';
// utils
import { fToNow } from 'src/utils/format-time';
// types
import { ThongBao } from 'src/types/notification';
// components
import Iconify from 'src/components/iconify';
// hooks
import { useNavigate } from 'react-router';

// ----------------------------------------------------------------------

type NotificationItemProps = {
  notification: ThongBao;
  onRead?: () => void; // Thêm prop này để gọi ngược về cha
};

export default function NotificationItem({ notification, onRead }: NotificationItemProps) {
  const navigate = useNavigate();

  const renderAvatar = (
    <ListItemAvatar>
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          bgcolor: notification.seen ? 'background.neutral' : 'primary.lighter',
        }}
      >
        {notification.caySamId ? (
          <Iconify
            icon="mdi:plant"
            width={24}
            sx={{ color: notification.seen ? 'text.secondary' : 'primary.main' }}
          />
        ) : (
          <Iconify
            icon="solar:bell-bing-bold"
            width={24}
            sx={{ color: notification.seen ? 'text.secondary' : 'primary.main' }}
          />
        )}
      </Stack>
    </ListItemAvatar>
  );

  const renderText = (
    <ListItemText
      disableTypography
      primary={reader(notification.tieuDe || 'Thông báo')}
      secondary={
        <Stack
          direction="row"
          alignItems="center"
          sx={{ typography: 'caption', color: 'text.disabled' }}
          divider={
            <Box
              sx={{ width: 2, height: 2, bgcolor: 'currentColor', mx: 0.5, borderRadius: '50%' }}
            />
          }
        >
          {fToNow(new Date(notification.ngayKhoiTao))}
          {notification.htTaiKhoan?.tenTaiKhoan && (
            <span>{notification.htTaiKhoan.tenTaiKhoan}</span>
          )}
        </Stack>
      }
    />
  );

  const renderUnReadBadge = !notification.seen && (
    <Box
      sx={{
        top: 26,
        width: 8,
        height: 8,
        right: 20,
        borderRadius: '50%',
        bgcolor: 'info.main',
        position: 'absolute',
      }}
    />
  );

  const renderContent = notification.noiDung && (
    <Typography
      variant="body2"
      sx={{
        mt: 1,
        p: 1.5,
        borderRadius: 1.5,
        bgcolor: 'background.neutral',
        color: 'text.secondary',
        display: '-webkit-box',
        WebkitLineClamp: 3,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {notification.noiDung}
    </Typography>
  );

  const handleClick = () => {
    // 1. Gọi API mark as read (thông qua hàm onRead từ cha)
    if (!notification.seen && onRead) {
      onRead();
    }

    // 2. Điều hướng nếu có ID cây sâm
    if (notification.caySamId) {
      navigate(`/caysam/${notification.caySamId}`, {
        state: {
          id: Number(notification.caySamId),
        },
      });
    }
  };

  return (
    <ListItemButton
      disableRipple
      onClick={handleClick}
      sx={{
        p: 2.5,
        alignItems: 'flex-start',
        borderBottom: (theme) => `dashed 1px ${theme.palette.divider}`,
        '&:hover': {
          bgcolor: 'action.hover',
        },
        // Highlight nhẹ nếu chưa xem (tùy chọn UI)
        ...(!notification.seen && {
            bgcolor: 'action.hover', 
        }),
      }}
    >
      {renderUnReadBadge}

      {renderAvatar}

      <Stack sx={{ flexGrow: 1 }}>
        {renderText}
        {renderContent}
      </Stack>
    </ListItemButton>
  );
}

// ----------------------------------------------------------------------

function reader(data: string) {
  return (
    <Box
      dangerouslySetInnerHTML={{ __html: data }}
      sx={{
        mb: 0.5,
        '& p': { typography: 'body2', m: 0 },
        '& a': { color: 'inherit', textDecoration: 'none' },
        '& strong': { typography: 'subtitle2' },
      }}
    />
  );
}